import { Button, ChevronLeft, ChevronRight, cn } from "@app/ui";
import { useEffect, useMemo, useState } from "react";
import { useAppLocale } from "../../lib/useAppLocale.js";
import { useI18n } from "../../lib/useI18n.js";

interface Props {
  value: Date | null;
  onSelect: (d: Date) => void;
  /** Live preview date from typed input — shown with a dashed ring, not committed. */
  previewValue?: Date | null;
}

export function DatePicker({ value, onSelect, previewValue }: Props) {
  const { t } = useI18n();
  const locale = useAppLocale();
  const [view, setView] = useState<Date>(() => previewValue ?? value ?? new Date());

  // Navigate to the preview date's month as the user types; otherwise follow the
  // committed value so a date typed into the field (or cleared on blur) keeps the
  // calendar in sync.
  useEffect(() => {
    if (previewValue) setView(previewValue);
    else if (value) setView(value);
  }, [previewValue, value]);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const monthLabel = view.toLocaleDateString(locale, { month: "long", year: "numeric" });
  const weekdays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => new Date(2024, 0, 7 + i).toLocaleDateString(locale, { weekday: "narrow" })),
    [locale],
  );

  function shiftMonth(delta: number) {
    setView((d) => new Date(d.getFullYear(), d.getMonth() + delta, 1));
  }

  function quick(addDays: number) {
    const d = new Date(today);
    d.setDate(today.getDate() + addDays);
    onSelect(d);
  }

  const monthStart = new Date(view.getFullYear(), view.getMonth(), 1);
  const monthEnd = new Date(view.getFullYear(), view.getMonth() + 1, 0);
  const padStart = monthStart.getDay();

  return (
    <div className="flex flex-col gap-3 rounded-md border border-input bg-card p-3 shadow-lg">
      <span className="text-[calc(10px*var(--app-text-scale))] uppercase tracking-wide text-muted-foreground">{t("datePicker.selectDate")}</span>

      <div className="flex items-center justify-between">
        <Button type="button" variant="ghost" size="icon" onClick={() => shiftMonth(-1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm font-medium">{monthLabel}</span>
        <Button type="button" variant="ghost" size="icon" onClick={() => shiftMonth(1)}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex gap-1">
        <Button type="button" size="sm" variant="outline" onClick={() => quick(0)}>{t("common.today")}</Button>
        <Button type="button" size="sm" variant="outline" onClick={() => quick(1)}>{t("datePicker.tomorrow")}</Button>
        <Button type="button" size="sm" variant="outline" onClick={() => quick(7)}>{t("datePicker.nextWeek")}</Button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-[calc(10px*var(--app-text-scale))] uppercase tracking-wide text-muted-foreground">
        {weekdays.map((d, i) => <div key={i} className="text-center">{d}</div>)}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: padStart }).map((_, i) => <div key={`p${i}`} />)}
        {Array.from({ length: monthEnd.getDate() }, (_, i) => i + 1).map((dayNum) => {
          const d = new Date(view.getFullYear(), view.getMonth(), dayNum);
          const isToday = sameDay(d, today);
          const isSelected = value ? sameDay(d, value) : false;
          const isPreview = !isSelected && previewValue != null && sameDay(d, previewValue);
          return (
            <button
              key={dayNum}
              type="button"
              onClick={() => onSelect(d)}
              className={cn(
                "flex h-9 w-9 items-center justify-center rounded-full text-sm transition-colors",
                isSelected && "bg-primary text-primary-foreground font-semibold",
                isPreview && "ring-2 ring-primary/60 ring-dashed text-primary bg-primary/10",
                !isSelected && !isPreview && isToday && "ring-1 ring-primary text-primary",
                !isSelected && !isPreview && !isToday && "hover:bg-accent",
              )}
            >
              {dayNum}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}
